import type {KeyValue, QueryParams, WritableKeyValue} from '@jetbrains/teamcity-ui/dist/utils'
import {
  objectEntries,
  objectToQuery,
  objectValues,
  resolveRelative,
} from '@jetbrains/teamcity-ui/dist/utils'
import {QueryStatus} from '@reduxjs/toolkit/query'
import {createSelector} from 'reselect'

import {CollapsibleBlock} from '../actions/collapsibleBlockTypes'
import {getPager} from '../components/common/Pager/Pager.selectors'
import {PagerGroup} from '../components/common/Pager/Pager.types'
import {getOffset} from '../components/common/Pager/Pager.utils'
import {
  getExpandAll,
  getFocusLine,
  getLogFilter,
} from '../components/packages/BuildLog/BuildLog.selectors'
import {ClassicChangePageTabName} from '../components/packages/Changes/ChangeDetailsTabs/ChangeDetailsTabs.types'
import {isCloudImageManageable} from '../components/pages/AgentsPages/AgentsPages.utils'
import {BranchesToShow} from '../components/pages/BuildTypePage/BuildTypeOverviewTab/BuildTypeBranches/CollapsibleBranchLines/CollapsibleBranchLines.types'
import {getBuildStatusTypeFromBuild} from '../hooks/build'
import type {State} from '../reducers/types'
import {getAgentPreviewsArg, getAgentsArg} from '../rest/agents'
import {BuildTypeProperties, getBuildTypeArg} from '../rest/buildTypes'
import {currentUserArg} from '../rest/currentUser'
import {overviewArg, sidebarArg} from '../rest/projects'
import type {StatusKey} from '../rest/schemata'
import {getStatusKey} from '../rest/schemata'
import {getTabsArg} from '../rest/tabs'
import {getOverviewHref} from '../routes'
import type {
  Branch,
  BuildType,
  GetSidebarProjectsNormalizedApiArg,
  Pipeline,
  User,
} from '../services/rest'
import {restApi} from '../services/rest'
import type {
  ActiveEntityProps,
  ActiveEntityURLProps,
  AgentId,
  AgentPoolId,
  AgentPreviewsHash,
  AgentRequestOptions,
  AgentTypeId,
  BuildArtifacts,
  BuildId,
  BuildState,
  BuildStatusType,
  BuildTypeId,
  ChangeId,
  CommentInfo,
  CompatibleAgent,
  DialogType,
  EntityDescription,
  Fetchable,
  FullPath,
  Id,
  InexactEntityParameters,
  NormalizedAgent,
  NormalizedBuild,
  NormalizedBuildTriggered,
  NormalizedCloudImage,
  NormalizedPipelineRun,
  NormalizedPipelineRunJob,
  NormalizedProject,
  PipelineId,
  ProjectId,
  ProjectOrBuildTypeNode,
  ProjectOrBuildTypeStatus,
  ProjectPathItem,
  ServerInfo,
  SidebarProject,
  Sorting,
  StatusRequest,
  Tab,
  TabId,
  TabParams,
  TabParamsKey,
  Tag,
  TestId,
  UrlExtension,
  UserId,
  WebLinks,
  WritableAgentPreviewsHash,
} from '../types'
import {
  BuildPageTabNamesEnum,
  BuildTypePageTabNamesEnum,
  getBuildTypeStatusRequest,
  ProjectPageTabNamesEnum,
  ROOT_PROJECT_ID,
  STAR_TAG,
  stringifyId,
  toPipelineId,
  toProjectId,
} from '../types'
import {getBranchParam, parseBranch, stringifyBranch} from '../utils/branchNames'
import {emptyArray, emptyArrayFetchable, getEmptyHash} from '../utils/empty'
import {resolveWebEntityLink, WebEntityType} from '../utils/entityLinks'
import {getPropertyFromList} from '../utils/getProperty'
import {notNull} from '../utils/guards'
import type {FullPathCacheItem} from '../utils/memoryCache'
import memoryCache from '../utils/memoryCache'
import {queryToObject} from '../utils/queryParams'
import {getTabParamsKey} from '../utils/tabs'
import {parseURL} from '../utils/url'
import type {UserProperty} from '../utils/userProperties'

// For some reason, calling a method on emptyArray makes type refinement work correctly
emptyArray.slice()
const getBuildTypesHash: (state: State) => KeyValue<BuildTypeId, BuildType> = state =>
  state.entities.buildTypes
const getSidebarData = (state: State, arg = sidebarArg) =>
  restApi.endpoints.getSidebarProjectsNormalized.select(arg)(state).data
export const getSidebarProjectIds = (state: State, arg?: GetSidebarProjectsNormalizedApiArg) =>
  getSidebarData(state, arg)?.result ?? emptyArray

export const getPipelinesHash: (state: State) => KeyValue<string, Pipeline> = state =>
  state.entities.pipelines

export const getPipeline = (
  state: State,
  id: string | null | undefined,
): Pipeline | null | undefined => (id != null ? getPipelinesHash(state)[id] : null)

const getPipelineRunsHash = (state: State): KeyValue<string, NormalizedPipelineRun> =>
  state.entities.pipelineRuns
const getPipelineRun = (
  state: State,
  id: string | null | undefined,
): NormalizedPipelineRun | null | undefined => (id != null ? getPipelineRunsHash(state)[id] : null)

const getPipelineRunJobsHash = (state: State): KeyValue<string, NormalizedPipelineRunJob> =>
  state.entities.pipelineRunJobs
const getPipelineRunJob = (
  state: State,
  id: string | null | undefined,
): NormalizedPipelineRunJob | null | undefined =>
  id != null ? getPipelineRunJobsHash(state)[id] : null

export const getBuildType = (
  state: State,
  id: BuildTypeId | null | undefined,
): BuildType | null | undefined => (id != null ? getBuildTypesHash(state)[id] : null)
export const getIsBuildTypePaused = (state: State, id?: BuildTypeId | null) =>
  id != null && (state.entities.buildTypes[id]?.paused ?? false)
export const getBuildTypeLinks = (
  state: State,
  id: BuildTypeId | null | undefined,
): WebLinks | null | undefined => (id ? state.entities.buildTypeLinks[id] : null)
export const getProjectLinks = (
  state: State,
  id: ProjectId | null | undefined,
): WebLinks | null | undefined => (id ? state.entities.projectLinks[id] : null)
export const getBuildTypeParameters = (
  state: State,
  id: BuildTypeId | null | undefined,
): InexactEntityParameters | null | undefined =>
  id ? state.entities.buildTypeParameters[id] : null
const getProjectParameters = (
  state: State,
  id: ProjectId | null | undefined,
): InexactEntityParameters | null | undefined => (id ? state.entities.projectParameters[id] : null)
export const isBuildTypeLoaded = (state: State, id: BuildTypeId | null | undefined): boolean =>
  id
    ? getBuildType(state, id) != null &&
      getBuildTypeLinks(state, id) != null &&
      getBuildTypeParameters(state, id) != null
    : false
const getOwnerProjectId = (
  state: State,
  buildTypeId: BuildTypeId | null | undefined,
): ProjectId | null | undefined => getBuildType(state, buildTypeId)?.projectId
export const getProjectsHash: (state: State) => KeyValue<ProjectId, NormalizedProject> = state =>
  state.entities.projects
export const getSidebarProjectsHash = (state: State, arg?: GetSidebarProjectsNormalizedApiArg) =>
  getSidebarData(state, arg)?.entities.sidebarProjects ?? getEmptyHash()
export const getProject = (
  state: State,
  id: ProjectId | null | undefined,
): NormalizedProject | null | undefined => (id != null ? getProjectsHash(state)[id] : null)

const getTabsResult = (state: State, key: TabParamsKey) =>
  restApi.endpoints.getTabs.select(getTabsArg(key))(state)

const getTabs: (state: State, key: TabParamsKey) => ReadonlyArray<Tab> = (state, key) =>
  getTabsResult(state, key).data ?? emptyArray

export const isProjectLoaded = (state: State, id: ProjectId | null | undefined): boolean =>
  id
    ? getProject(state, id) != null &&
      getProjectLinks(state, id) != null &&
      getProjectParameters(state, id) != null
    : false
export const isArchivedProject = (state: State, id: ProjectId | null | undefined): boolean => {
  const project = getProject(state, id)
  return project ? project.archived : false
}

export const getOverviewResult = restApi.endpoints.getSidebarProjectsNormalized.select(overviewArg)
export const getOverviewProjectsHash = (state: State): KeyValue<ProjectId, SidebarProject> =>
  getOverviewResult(state).data?.entities.sidebarProjects ?? getEmptyHash()
export const getOverviewProject = (
  state: State,
  id: ProjectId | null | undefined,
): SidebarProject | null | undefined => (id != null ? getOverviewProjectsHash(state)[id] : null)

export const getShowQueuedBuildsInBuildsList = (state: State): boolean =>
  state.showQueuedBuildsInBuildsList
export const getIsSakuraUI = (state: State): boolean => state.isSakuraUI

export const getProjectsReady = (state: State, projectId = ROOT_PROJECT_ID) =>
  state.projectsReady[projectId] === true

export const getSidebarProjectsReady = (state: State, arg?: GetSidebarProjectsNormalizedApiArg) =>
  getSidebarData(state, arg) != null

export const getPathBuildTypeArg = (buildTypeId: BuildTypeId) =>
  getBuildTypeArg(buildTypeId, {withLinks: true})

export const getPathBuildType = (state: State, id: BuildTypeId) => {
  const buildTypePathResult = restApi.endpoints.getPathBuildTypeNormalized.select(
    getPathBuildTypeArg(id),
  )(state)

  if (buildTypePathResult.status === QueryStatus.fulfilled) {
    const {result: buildTypeId, entities} = buildTypePathResult.data!
    return entities?.buildTypes?.[buildTypeId]
  }

  return null
}

const getPathBuildTypeLinks = (state: State, id: BuildTypeId) => {
  const buildTypePathResult = restApi.endpoints.getPathBuildTypeNormalized.select(
    getPathBuildTypeArg(id),
  )(state)

  if (buildTypePathResult.status === QueryStatus.fulfilled) {
    const {result: buildTypeId, entities} = buildTypePathResult.data!
    return entities?.buildTypeLinks?.[buildTypeId]
  }

  return null
}

const getFullPathPart = (
  state: State,
  node: ProjectOrBuildTypeNode | null | undefined,
  onlyFavorites?: boolean | null | undefined,
): FullPathCacheItem => {
  const result = {
    fullPath: emptyArray,
    isFullyLoaded: false,
  }

  if (node == null || node.nodeType === 'all' || node.id === ROOT_PROJECT_ID) {
    result.isFullyLoaded = true
    return result
  }

  const cacheKey = `${node.nodeType}_${stringifyId(node.id)}`
  const cached: FullPathCacheItem | null | undefined = memoryCache.get('fullPath', cacheKey)

  if (cached?.isFullyLoaded === true) {
    return cached
  }

  if (node.nodeType === 'bt') {
    const buildType = getBuildType(state, node.id) ?? getPathBuildType(state, node.id)

    if (buildType == null) {
      result.fullPath = [
        {
          id: node.id,
          name: stringifyId(node.id),
          projectId: ROOT_PROJECT_ID,
        },
      ]
    } else {
      const links = getBuildTypeLinks(state, node.id) ?? getPathBuildTypeLinks(state, node.id)
      const item: BuildType = links != null ? {...buildType, ...links} : {...buildType}
      const parentPart = getFullPathPart(state, {
        nodeType: 'project',
        id: buildType.projectId,
      })
      result.fullPath = parentPart.fullPath.concat(item)
      result.isFullyLoaded = parentPart.isFullyLoaded
    }
  } else if (node.nodeType === 'pipeline') {
    const pipeline = getPipeline(state, node.id)
    if (pipeline == null) {
      result.fullPath = [
        {
          id: node.id,
          name: stringifyId(node.id),
          projectId: ROOT_PROJECT_ID,
        },
      ]
    } else {
      const parentPart = getFullPathPart(state, {
        nodeType: 'project',
        id: pipeline.parentProject.id,
      })
      result.fullPath = parentPart.fullPath.concat(pipeline)
    }
  } else if (node.nodeType === 'jobBatch') {
    const buildType = getBuildType(state, node.id)
    if (buildType == null) {
      result.fullPath = [
        {
          id: node.id,
          name: stringifyId(node.id),
          projectId: ROOT_PROJECT_ID,
        },
      ]
    } else {
      const runJob = getPipelineRunJob(state, node.jobId)
      const pipelineId = getPipelineRun(state, runJob?.pipelineRun)?.pipeline
      const batchItem = {
        id: buildType.id,
        name: buildType.name,
        pipelineId,
        runId: runJob?.pipelineRun,
        jobId: node.jobId,
        batchId: stringifyId(node.batchId),
      }
      const parentPart = getFullPathPart(state, {
        nodeType: 'job',
        id: node.jobId,
      })
      result.fullPath = parentPart.fullPath.concat(batchItem)
    }
  } else if (node.nodeType === 'job') {
    const job = getPipelineRunJob(state, node.id)
    if (job == null) {
      result.fullPath = [
        {
          id: node.id,
          name: stringifyId(node.id),
          projectId: ROOT_PROJECT_ID,
        },
      ]
    } else {
      const runJob = getPipelineRunJob(state, node.id)
      const pipelineId = getPipelineRun(state, runJob?.pipelineRun)?.pipeline ?? toPipelineId('')
      const jobItem = {
        ...job,
        id: stringifyId(job.build.id),
        pipelineId,
        runId: runJob?.pipelineRun,
      }
      const parentPart = getFullPathPart(state, {
        nodeType: 'pipeline',
        id: pipelineId,
      })
      result.fullPath = parentPart.fullPath.concat(jobItem)
    }
  } else if (node.nodeType === 'project') {
    const project = onlyFavorites ? getOverviewProject(state, node.id) : getProject(state, node.id)

    if (project == null) {
      return result
    }

    const links = getProjectLinks(state, node.id)
    const item: ProjectPathItem = links != null ? {...project, ...links} : {...project}
    const {parentProjectId} = project

    if (parentProjectId == null) {
      result.fullPath = [item]
      result.isFullyLoaded = true
    } else {
      const parentPart = getFullPathPart(
        state,
        {
          nodeType: 'project',
          id: parentProjectId,
        },
        onlyFavorites,
      )
      result.fullPath = parentPart.fullPath.concat(item)
      result.isFullyLoaded = parentPart.isFullyLoaded
    }
  } else {
    return result
  }

  if (result.fullPath.length === cached?.fullPath.length) {
    return cached
  }

  memoryCache.set('fullPath', cacheKey, result)
  return result
}

export const getFullPath = (
  state: State,
  node: ProjectOrBuildTypeNode | null | undefined,
  onlyFavorites?: boolean | null | undefined,
): FullPath => getFullPathPart(state, node, onlyFavorites).fullPath

const getCurrentUserResult = restApi.endpoints.getUser.select(currentUserArg)
const getCurrentUser = (state: State): User | undefined => getCurrentUserResult(state).data
export const getIsCurrentUserResolved = (state: State): boolean =>
  getCurrentUserResult(state).data != null
export const getCurrentUserId = (state: State): UserId | null | undefined =>
  getCurrentUser(state)?.id
export const getIsGuestOrRoot: (state: State) => boolean = state => {
  const id = getCurrentUserId(state)
  return id == null || id < 0
}
export const getUserProperty = (state: State, name: UserProperty) =>
  getIsGuestOrRoot(state)
    ? state.fallbackUserProperties[name]
    : getPropertyFromList(name, getCurrentUser(state)?.properties)

export const getBuildsData = (
  state: State,
  locator: string | null | undefined,
): Fetchable<ReadonlyArray<BuildId>> =>
  (locator != null && state.builds[locator]) || emptyArrayFetchable
export const getBuildsHash: (state: State) => KeyValue<BuildId, NormalizedBuild | null> = state =>
  state.entities.builds
export const getBuild = (
  state: State,
  id: BuildId | null | undefined,
): NormalizedBuild | null | undefined => (id != null ? getBuildsHash(state)[id] : null)
export const getBuildTestOccurrencesCount = (
  state: State,
  id: BuildId | null | undefined,
): number | null | undefined => (id != null ? state.entities.buildsTestOccurrencesCount[id] : null)

export function getBuildAgent(state: State, id: BuildId): NormalizedAgent | null | undefined {
  const build = getBuild(state, id)
  return build?.state === 'queued'
    ? build?.plannedAgent?.typeId === 0
      ? null
      : build?.plannedAgent
    : build?.agent
}

export const makeBuildBranchSelector = () =>
  createSelector(
    [
      (state: State, id: BuildId | null | undefined) => getBuild(state, id)?.branchName,
      (state: State, id: BuildId | null | undefined) => getBuild(state, id)?.defaultBranch,
    ],
    (branchName, defaultBranch) => {
      const parsedBranch = parseBranch(branchName)
      if (parsedBranch) {
        return {
          ...parsedBranch,
          default: defaultBranch,
        }
      }
      return parsedBranch
    },
  )

export const getSnapshotDependencies = (state: State): KeyValue<BuildId, ReadonlyArray<BuildId>> =>
  state.entities.snapshotDependencies
export const getBuildSnapshotDependencies = (
  state: State,
  id: BuildId | null | undefined,
): ReadonlyArray<BuildId> =>
  id != null ? (getSnapshotDependencies(state)[id] ?? emptyArray) : emptyArray

export const getSnapshotDependenciesLoaded = (
  state: State,
  id: BuildId | null | undefined,
): boolean => id != null && state.entities.snapshotDependencies[id] != null
export const getBuildTriggered = (
  state: State,
  id: BuildId | null | undefined,
): NormalizedBuildTriggered | null | undefined => {
  const build = getBuild(state, id)
  return build ? build.triggered : null
}
export const getBuildComment = (
  state: State,
  id: BuildId | null | undefined,
):
  | (CommentInfo &
      Readonly<{
        buildTypeId: BuildTypeId
      }>)
  | null
  | undefined => {
  const build = getBuild(state, id)
  return build
    ? {
        buildTypeId: build.buildType,
        ...build.comment,
      }
    : null
}
export const getBuildPinInfo = (
  state: State,
  id: BuildId | null | undefined,
):
  | (CommentInfo &
      Readonly<{
        buildTypeId: BuildTypeId
      }>)
  | null
  | undefined => {
  const build = getBuild(state, id)
  return build
    ? {
        buildTypeId: build.buildType,
        ...build.pinInfo,
      }
    : null
}
export const getBuildCanceledInfo = (
  state: State,
  id: BuildId | null | undefined,
): CommentInfo | null | undefined => {
  const build = getBuild(state, id)
  return build ? build.canceledInfo : null
}
export const makeGetMaybeBuilds: () => (
  state: State,
  locator: string | null | undefined,
) => ReadonlyArray<NormalizedBuild | null | undefined> = () =>
  createSelector(
    [
      getBuildsHash,
      (state: State, locator: string | null | undefined) => getBuildsData(state, locator).data,
    ],
    (hash: KeyValue<BuildId, NormalizedBuild | null>, data: ReadonlyArray<BuildId>) =>
      data.map(id => hash[id]),
  )
export const makeGetBuilds: () => (
  state: State,
  locator: string | null | undefined,
) => ReadonlyArray<NormalizedBuild> = () =>
  createSelector(makeGetMaybeBuilds(), builds => {
    const result = builds.filter(notNull)
    return result.length > 0 ? result : emptyArray
  })
// This selector is suitable only for components that use the main locator of page (pager, filters, etc.)
const getBuilds = makeGetBuilds()

export const getBuildsLocatorWithPage = (state: State, locator: string, page: number) => {
  const pager = getPager(state, PagerGroup.BUILD)
  const offset = getOffset(pager, page)
  const count = pager.pageSize
  return `${locator},start:${offset},count:${count},lookupLimit:${String(pager.lookupLimit)}`
}

export const getNoMatchingWords = (source: string, words: string[]): string[] => {
  const [firstWord] = words
  const notFoundWords: Array<string> = []

  if (!source?.includes(firstWord)) {
    return words
  }

  if (source) {
    let startIndex = 0
    let matchedWordIndex: number | void

    words.forEach(word => {
      if (matchedWordIndex !== -1) {
        matchedWordIndex = source.indexOf(word, startIndex)
      }

      if (matchedWordIndex === -1) {
        notFoundWords.push(word)
      } else {
        startIndex = matchedWordIndex + word.length
      }
    })
  }

  return notFoundWords
}

export const getBuildsFilteredBySearchQuery = (
  state: State,
  builds: readonly (NormalizedBuild | null | undefined)[],
  searchQuery: string | null | undefined,
): NormalizedBuild[] => {
  const words: Array<string> | undefined = searchQuery?.toLowerCase().split(' ').filter(Boolean)
  return builds.filter((build): build is NormalizedBuild => {
    const isNotNull = build != null

    if (isNotNull && words?.length) {
      const fullPath = getFullPath(state, {
        nodeType: 'bt',
        id: build?.buildType,
      })
      const fullPathString = fullPath
        .map(({name}) => name)
        .join(' ')
        .toLowerCase()
      const noMatchingWords = getNoMatchingWords(fullPathString, words)

      return !noMatchingWords.length
    }

    return isNotNull
  })
}

const filterBuildsByPage = (
  currentPage: number,
  pageSize: number,
  builds: readonly NormalizedBuild[],
) => builds.slice((currentPage - 1) * pageSize, currentPage * pageSize)

export const getBuildsFilteredByPage: (
  state: State,
  builds: readonly NormalizedBuild[],
  currentPage: number,
) => NormalizedBuild[] = createSelector(
  (_: State, __: readonly NormalizedBuild[], currentPage: number) => currentPage,
  (state: State) => getPager(state, PagerGroup.BUILD).pageSize,
  (state: State, builds: readonly NormalizedBuild[]) => builds,
  filterBuildsByPage,
)

export const getBuildsLoading = (state: State, locator?: string | null): boolean =>
  getBuildsData(state, locator).loading
export const getBuildsInited = (state: State, locator?: string): boolean =>
  getBuildsData(state, locator).inited
export const getBuildsHasError: (state: State, locator?: string | null) => boolean = (
  state,
  locator,
) => getBuildsData(state, locator).error != null
export const getBuildsReady = (state: State, locator?: string | null): boolean =>
  getBuildsData(state, locator).ready
export const getSomeBuildsLoaded = (state: State, locator: string | null | undefined): boolean =>
  getBuildsData(state, locator).data.length > 0
const getBuildArtifactsHash = (state: State): KeyValue<BuildId, NormalizedBuild | null> =>
  state.entities.buildArtifacts

const hasArtifactsInBuild = (
  build: BuildArtifacts | null | undefined,
): boolean | null | undefined => {
  const {artifacts} = build || {}
  return artifacts?.count != null ? artifacts.count > 0 : null
}

export const getHasArtifacts = (state: State, id: BuildId): boolean | null | undefined => {
  const hasArtifacts = hasArtifactsInBuild(getBuild(state, id))
  return hasArtifacts != null ? hasArtifacts : hasArtifactsInBuild(getBuildArtifactsHash(state)[id])
}
export const getHasArtifactsState = (state: State, id: BuildId): BuildState | null | undefined => {
  const build = getBuild(state, id)
  const hasArtifacts = hasArtifactsInBuild(build)
  const buildToCheck: NormalizedBuild | null | undefined =
    hasArtifacts != null ? build : getBuildArtifactsHash(state)[id]
  return buildToCheck?.state
}

const getBuildCompatibleAgentsHash = (state: State): KeyValue<BuildId, CompatibleAgent | null> =>
  state.entities.compatibleAgents

export const getBuildCompatibleAgents = (
  state: State,
  id: BuildId,
): CompatibleAgent | null | undefined => getBuildCompatibleAgentsHash(state)[id]

export const isDialogShown: (state: State, dialogId?: string, type?: DialogType) => boolean = (
  state,
  dialogId = '',
  type,
) => state.dialog.opened === true && state.dialog.type === type && state.dialog.id === dialogId
export const isDialogProcessing = (state: State): boolean => !!state.dialog.processing
export const dialogHasError = (state: State): boolean => !!state.dialog.error

export const getAllTags = (state: State, buildId: BuildId | null | undefined): ReadonlyArray<Tag> =>
  getBuild(state, buildId)?.tags?.tag ?? emptyArray

// If you need this array as a prop, use makeGetTags factory instead
export const getTags = (state: State, buildId: BuildId | null | undefined): ReadonlyArray<Tag> =>
  getAllTags(state, buildId).filter(tag => !tag.private)
export const getTagsCount = (state: State, buildId: BuildId | null | undefined): number =>
  getTags(state, buildId).length
export const getTagsLabel = (state: State, buildId: BuildId): string => {
  const count = getTagsCount(state, buildId)

  switch (count) {
    case 0:
      return ''

    case 1:
      return getTags(state, buildId)[0].name

    default:
      return count.toString()
  }
}
export const makeGetTags: () => (
  state: State,
  id: BuildId | null | undefined,
) => ReadonlyArray<Tag> = () => createSelector(getAllTags, tags => tags.filter(tag => !tag.private))
export const makeGetTagNames: () => (
  state: State,
  id: BuildId | null | undefined,
) => ReadonlyArray<string> = () => createSelector(makeGetTags(), tags => tags.map(tag => tag.name))
export const getIsStarred = (state: State, buildId: BuildId | null | undefined): boolean => {
  if (buildId == null) {
    return false
  }

  const {tags} = getBuild(state, buildId) || {}
  return tags?.tag?.some(tag => tag.private && tag.name === STAR_TAG) || false
}
export const getIsStopping = (state: State, buildId: BuildId): boolean =>
  !!state.stoppingBuilds[buildId]
export const getHasDependencies = (state: State, buildId: BuildId | null | undefined): boolean => {
  const deps = getBuild(state, buildId)?.['snapshot-dependencies']
  return deps?.count != null ? deps.count > 0 : false
}

const getAgentsHash = (state: State): KeyValue<AgentId, NormalizedAgent | null> =>
  state.entities.agents

export const getAgent = (state: State, id: AgentId): NormalizedAgent | null | undefined =>
  getAgentsHash(state)[id]
export const getAgents = createSelector(
  (state: State, locator: string, options: AgentRequestOptions) =>
    restApi.endpoints.getAllAgentsNormalized.select(getAgentsArg(locator, options))(state).data,
  data => data?.result.map(id => data.entities.agents?.[id]).filter(notNull) ?? emptyArray,
)
export const getAgentsReady = (state: State, locator: string, options: AgentRequestOptions) =>
  restApi.endpoints.getAllAgentsNormalized.select(getAgentsArg(locator, options))(state).data !=
    null && getProjectsReady(state)
export const getSingleAgent = (
  state: State,
  agentId: AgentId,
): NormalizedAgent | null | undefined => state.entities.agent[agentId]
const getAgentPreviewsResult =
  restApi.endpoints.getAllAgentPreviewsNormalized.select(getAgentPreviewsArg())
export const getAgentPreviewsReady = (state: State): boolean =>
  getAgentPreviewsResult(state).data != null
const getAgentPreviews: (state: State) => AgentPreviewsHash | null | undefined = createSelector(
  getAgentPreviewsReady,
  (state: State) => getAgentPreviewsResult(state).data?.result ?? emptyArray,
  (state: State) => state.entities.agentPreviews,
  (
    ready: boolean,
    agentIds: ReadonlyArray<AgentId>,
    entities: AgentPreviewsHash,
  ): AgentPreviewsHash | null | undefined =>
    ready
      ? agentIds.reduce((hash: WritableAgentPreviewsHash, agentId: AgentId) => {
          hash[agentId] = entities[agentId]
          return hash
        }, {})
      : null,
)
export const getAllAgentPreviews = (state: State): AgentPreviewsHash =>
  getAgentPreviews(state) || getEmptyHash()
export const getSingleCloudImage = (
  state: State,
  agentTypeId: AgentTypeId,
): NormalizedCloudImage | null | undefined => state.entities.cloudImage[agentTypeId]
export const getSorting = (state: State): Sorting => state.sorting
export const getSortingDimension = (state: State): string => getSorting(state).dimension
export const getSortingDescending = (state: State): boolean => getSorting(state).descending
export const getAgentInCloud = (state: State, agentId: AgentId): boolean =>
  state.agentsInCloud[agentId] ?? false

const getBlocksHash = (state: State, block: CollapsibleBlock): KeyValue<string | Id, boolean> =>
  state.blocks[block] ?? getEmptyHash()

export const getBlocks: (
  state: State,
  collapsibleBlock: CollapsibleBlock,
) => ReadonlyArray<string | Id> = createSelector(getBlocksHash, hash =>
  Object.keys(hash).filter(id => hash[id]),
)
const getIsBlockIncluded: (
  state: State,
  block: CollapsibleBlock,
  id: string | Id,
  defaultTrue?: boolean,
) => boolean = (state, block, id, defaultTrue) =>
  getBlocksHash(state, block)[id] ?? defaultTrue === true

export const getOverviewData = (state: State): ReadonlyArray<ProjectId> =>
  getOverviewResult(state).data?.result ?? emptyArray
export const makeGetFavoriteSubProjectIds: () => (
  state: State,
  projectId: ProjectId,
) => ReadonlyArray<ProjectId> = () =>
  createSelector(
    getOverviewProjectsHash,
    getOverviewData,
    (_: State, projectId: ProjectId) => projectId,
    (projects, favoriteIds, projectId) => {
      function isChild(childId: ProjectId | null | undefined): boolean {
        if (childId == null || childId === ROOT_PROJECT_ID) {
          return false
        }

        const {parentProjectId} = projects[childId] || {}

        if (parentProjectId === projectId) {
          return true
        }

        if (parentProjectId != null && favoriteIds.includes(parentProjectId)) {
          return false
        }

        return isChild(parentProjectId)
      }

      return favoriteIds.filter(isChild)
    },
  )
export const makeGetIsSubprojectsExist: () => (
  state: State,
  projectId: ProjectId,
) => boolean = () =>
  createSelector(
    getProjectsHash,
    (_: State, projectId: ProjectId) => projectId,
    (projects, projectId) =>
      objectValues(projects).some(project => project?.parentProjectId === projectId),
  )
export const makeGetIsFavoriteSubprojectsExist: () => (
  state: State,
  projectId: ProjectId,
) => boolean = () =>
  createSelector(
    getOverviewProjectsHash,
    (_: State, projectId: ProjectId) => projectId,
    (projects, projectId) =>
      objectValues(projects).some(project => project?.parentProjectId === projectId),
  )
export const makeGetDirectNotArchivedSubProjectIds: () => (
  state: State,
  projectId: ProjectId,
) => ReadonlyArray<ProjectId> = () =>
  createSelector(
    getProjectsHash,
    (_: State, projectId: ProjectId) => projectId,
    (projects, projectId) =>
      objectEntries(projects)
        .filter(
          ([_, project]) =>
            project?.parentProjectId === projectId &&
            project?.virtual !== true &&
            !project.holdsPipeline &&
            !project.archived,
        )
        .map(([key]) => toProjectId(key)),
  )
export const getIsEmptyProject = (state: State, projectId: ProjectId) => {
  const project = getProject(state, projectId)
  return (
    !project?.buildTypes?.buildType?.length &&
    !project?.pipelines?.pipeline?.length &&
    !objectValues(getProjectsHash(state)).some(
      possibleChild => possibleChild?.parentProjectId === projectId,
    )
  )
}
export const makeGetDirectNotArchivedFavoriteSubProjectIds: () => (
  state: State,
  projectId: ProjectId,
) => ReadonlyArray<ProjectId> = () =>
  createSelector(
    getOverviewProjectsHash,
    (_: State, projectId: ProjectId) => projectId,
    (projects, projectId) =>
      objectEntries(projects)
        .filter(
          ([_, project]) =>
            project?.parentProjectId === projectId &&
            project?.virtual !== true &&
            !project.archived,
        )
        .map(([key]) => toProjectId(key)),
  )
export const getHasDependants = (state: State, buildId: BuildId): boolean | null | undefined =>
  state.haveDependants[buildId]
export const getBuildTypesFromLoadedBuild: (
  state: State,
  locator?: string | null,
) => ReadonlyArray<BuildTypeId> = createSelector(getBuilds, builds =>
  builds.reduce<BuildTypeId[]>(
    (x, y) => (y.buildType != null && x.includes(y.buildType) ? x : [...x, y.buildType]),
    [],
  ),
)
export const getIsUpdating = (state: State): boolean => Boolean(state.buildsFilters.updating)

export const getBuildStatusType = (state: State, buildId: BuildId | undefined): BuildStatusType =>
  getBuildStatusTypeFromBuild(getBuild(state, buildId))
export const getIsBuildDetached = (state: State, buildId: BuildId): boolean | null | undefined =>
  getBuild(state, buildId)?.detachedFromAgent

export const getIsProjectOnOverview = (state: State, id: ProjectId | null | undefined): boolean => {
  const toggling = id ? state.togglingOverview.project[id] : null
  return toggling != null ? toggling : id != null && getOverviewData(state).includes(id)
}
export const getIsBuildTypeOnOverview = (
  state: State,
  id: BuildTypeId | null | undefined,
): boolean => {
  const toggling = id ? state.togglingOverview.bt[id] : null

  if (toggling != null) {
    return toggling
  }

  const projectId = getOwnerProjectId(state, id)

  if (!getIsProjectOnOverview(state, projectId)) {
    return false
  }

  const buildTypes = getOverviewProject(state, projectId)?.buildTypes?.buildType
  return buildTypes && id != null ? buildTypes.some(buildType => buildType.id === id) : false
}

export const getStatuses = (state: State): KeyValue<StatusKey, ProjectOrBuildTypeStatus> =>
  state.buildTypeStatuses
export const getStatus = (
  state: State,
  request: StatusRequest | null | undefined,
): ProjectOrBuildTypeStatus | null | undefined =>
  request && getStatuses(state)[getStatusKey(request)]
export const getStatusByKey = (
  state: State,
  statusKey: StatusKey,
): ProjectOrBuildTypeStatus | null | undefined => getStatuses(state)[statusKey]
export const getIsBuildStarting = (
  state: State,
  buildTypeId: BuildTypeId,
  branch: Branch | null | undefined,
): boolean =>
  Boolean(state.startingBuilds[getStatusKey(getBuildTypeStatusRequest(buildTypeId, branch))])
export const getExtensionEndpoint = (state: State, name: string) =>
  state.urlExtensions.find(e => e.name === name)
export const getExtensionEndpointsByKind = (
  state: State,
  kind: string,
): ReadonlyArray<UrlExtension<any>> => state.urlExtensions.filter(e => e.kind === kind)
export const generateClassicUILinkWithQuery = (
  href: string | null | undefined,
  params: QueryParams = {},
  hash?: string,
): string => {
  const url = parseURL(href ?? '')
  url.search = objectToQuery({...queryToObject(url.search), ...params, fromSakuraUI: 'true'})

  if (hash != null) {
    url.hash = hash
  }

  return url.href
}
export const getClassicUIFavoriteBuildsLink = (): string =>
  generateClassicUILinkWithQuery(resolveRelative('/favoriteBuilds.html'))
export const getClassicUIAgentsLink = (tab?: string | null | undefined): string =>
  generateClassicUILinkWithQuery(resolveRelative('/agents.html'), {
    tab,
  })
export const getClassicUIAgentLink = (
  id: AgentId,
  tab?: TabId | null | undefined,
  kind?: string | null | undefined,
): string =>
  generateClassicUILinkWithQuery(resolveRelative('/agentDetails.html'), {
    id: String(id),
    tab: tab != null ? stringifyId(tab) : null,
    kind,
  })
export const getClassicUIAgentPoolLink = (id: AgentPoolId): string =>
  generateClassicUILinkWithQuery(
    resolveRelative('/agents.html'),
    {
      tab: 'agentPools',
    },
    String(id),
  )
export const getClassicUICloudImageLink = (
  agentTypeId: AgentTypeId,
  tab?: string | null | undefined,
  kind?: string | null | undefined,
): string =>
  generateClassicUILinkWithQuery(
    resolveRelative('/agentDetails.html', {
      agentTypeId: stringifyId(agentTypeId),
      tab,
      kind,
    }),
  )
export const getClassicUITestHistoryLink = ({
  testId,
  projectId,
  buildTypeId,
  branch,
}: {
  testId: TestId | null | undefined
  projectId: ProjectId | null | undefined
  buildTypeId?: BuildTypeId | null | undefined
  branch?: Branch | null | undefined
}): string => {
  const params: WritableKeyValue<string, string | null | undefined> = {
    testNameId: stringifyId(testId),
    projectId: stringifyId(projectId),
    buildTypeId: buildTypeId != null ? stringifyId(buildTypeId) : null,
    tab: 'testDetails',
  }

  if (branch != null) {
    params[`branch_${params.projectId}`] = stringifyBranch(branch)
  }

  return generateClassicUILinkWithQuery(resolveRelative('/project.html'), params)
}

export const getClassicUIChangeLink = ({
  changeId,
  buildTypeId,
  personal,
  tab,
}: {
  changeId: ChangeId
  buildTypeId?: BuildTypeId | null | undefined
  personal?: boolean | null | undefined
  tab?: ClassicChangePageTabName | null | undefined
}): string =>
  generateClassicUILinkWithQuery(
    resolveRelative('/viewModification.html', {
      modId: stringifyId(changeId),
      buildTypeId: buildTypeId != null ? stringifyId(buildTypeId) : null,
      personal: personal != null ? String(personal) : null,
      tab: tab ?? ClassicChangePageTabName.FILES,
    }),
  )

export const getClassicUIChangesLink = (): string =>
  generateClassicUILinkWithQuery(resolveRelative('/changes.html'))

export const getClassicUIDiffBetweenTwoFiles = ({
  changeId,
  personal,
  file,
}: Readonly<{
  changeId: ChangeId
  personal?: boolean
  file: string
}>): string =>
  resolveRelative(`/diffView.html`, {
    id: stringifyId(changeId),
    vcsFileName: file,
    personal: personal != null ? personal.toString() : 'false',
  })

export const getClassicUIQueueLink = (): string =>
  generateClassicUILinkWithQuery(resolveRelative('/queue.html'))

export const getClassicUIInvestigationsLink = (): string =>
  generateClassicUILinkWithQuery(resolveRelative('/investigations.html', {init: '1'}))

export const makeGetClassicUILinkWithBranch = (
  {projectId, buildTypeId, buildId}: ActiveEntityURLProps,
  branch?: Branch | null,
  params?: KeyValue<string, string | null | undefined> | null,
): ((state: State) => string) =>
  createSelector(
    (state: State) => projectId ?? getOwnerProjectId(state, buildTypeId),
    getFocusLine,
    getLogFilter,
    getExpandAll,
    (branchProjectId, focusLine, logFilter, expandAll) => {
      let webEntityType: WebEntityType | null = null
      let id: Id | null = null

      if (!buildId && buildTypeId) {
        webEntityType = WebEntityType.BUILD_TYPE
        id = buildTypeId
      } else if (projectId && projectId !== ROOT_PROJECT_ID) {
        webEntityType = WebEntityType.PROJECT
        id = projectId
      }

      const href =
        buildId != null
          ? resolveRelative('/viewLog.html')
          : ((webEntityType && resolveWebEntityLink(webEntityType, id, false)) ??
            resolveRelative('/overview.html'))
      const branchParam: string = buildId != null ? 'buildBranch' : getBranchParam(branchProjectId)
      return generateClassicUILinkWithQuery(href, {
        ...params,
        buildId: buildId != null ? stringifyId(buildId) : null,
        buildTypeId: buildTypeId != null ? stringifyId(buildTypeId) : null,
        [branchParam]: branch && stringifyBranch(branch),
        _focus: focusLine?.toString() ?? undefined,
        filter: logFilter?.toString() ?? undefined,
        expand: expandAll ? 'all' : undefined,
      })
    },
  )
export const getShowQueuedBuildsPerBranch = (state: State, key: StatusKey): boolean =>
  state.showQueuedBuildsPerBranch[key] ?? false
export const getShowQueuedBuildsCount = (
  state: State,
  buildTypeId: BuildTypeId,
  branch?: Branch | null,
): number | undefined =>
  state.showQueuedBuildsCount[
    getStatusKey({
      type: 'bt',
      branch,
      id: buildTypeId,
    })
  ]
export const getShowQueuedBuildsInProject = (state: State, buildTypeId: BuildTypeId): boolean =>
  state.showQueuedBuildsInProject[buildTypeId] ?? false
export const isBuildLoaded = (state: State, id: BuildId | null | undefined): boolean =>
  id ? getBuild(state, id) != null : false
const getProjectDescriptionsHash = (state: State): KeyValue<ProjectId, EntityDescription> =>
  state.entities.projectDescription
export const getProjectDescription = (
  state: State,
  id: ProjectId | null | undefined,
): string | null | undefined =>
  id != null ? getProjectDescriptionsHash(state)[id]?.description : null
const getBuildTypeDescriptionsHash = (state: State): KeyValue<BuildTypeId, EntityDescription> =>
  state.entities.buildTypeDescription
export const getBuildTypeDescription = (
  state: State,
  id: BuildTypeId | null | undefined,
): string | null | undefined =>
  id != null ? getBuildTypeDescriptionsHash(state)[id]?.description : null

export const buildTypeBranchesSectionCollapsedKey = (
  buildTypeId: BuildTypeId,
  branchesToShow: BranchesToShow,
): string => `${stringifyId(buildTypeId)}_${branchesToShow}`
export const isBuildTypeBranchesSectionCollapsed = (
  state: State,
  buildTypeId: BuildTypeId | null | undefined,
  branchesToShow: BranchesToShow,
): boolean => {
  if (buildTypeId == null || branchesToShow === BranchesToShow.DEFAULT) {
    return false
  }

  return getIsBlockIncluded(
    state,
    CollapsibleBlock.COLLAPSED_BRANCHES_SECTION,
    buildTypeBranchesSectionCollapsedKey(buildTypeId, branchesToShow),
  )
}
export const isBuildTypeCollapsed = (
  state: State,
  buildTypeId: BuildTypeId,
  defaultCollapsed: boolean,
): boolean =>
  getIsBlockIncluded(state, CollapsibleBlock.COLLAPSED_BUILDTYPE, buildTypeId, defaultCollapsed)

export const isPipelineCollapsed = (
  state: State,
  pipelineId: PipelineId,
  defaultCollapsed: boolean,
): boolean =>
  getIsBlockIncluded(state, CollapsibleBlock.COLLAPSED_PIPELINE, pipelineId, defaultCollapsed)

export const getIsSubprojectCollapsed = (
  state: State,
  projectId: ProjectId,
  defaultCollapsed = true,
): boolean =>
  getIsBlockIncluded(state, CollapsibleBlock.COLLAPSED_SUBPROJECT, projectId, defaultCollapsed)

export const getServerInfo = (state: State): ServerInfo | null | undefined => state.serverInfo
export const getVersion = (state: State) => getServerInfo(state)?.version ?? 'SNAPSHOT'
export const getTabUrl = (
  {projectId, buildTypeId, buildId, pipelineId, runId, jobId, batchId}: ActiveEntityProps,
  tab: TabId,
): string | undefined => {
  if (projectId == null && buildTypeId == null && buildId == null && pipelineId == null) {
    return undefined
  }

  const currentSearch = new URLSearchParams(location.search)
  const tabValue = String(tab)

  if (pipelineId) {
    currentSearch.set('tab', tabValue)
    if (jobId) {
      currentSearch.set('job', jobId)
    }
    if (batchId) {
      currentSearch.set('pageFullLogBuildId', batchId)
    }
  } else if (buildId != null) {
    currentSearch.set('buildTab', tabValue)
  } else if (buildTypeId) {
    currentSearch.set('buildTypeTab', tabValue)
  } else {
    currentSearch.set('projectTab', tabValue)
  }

  return `${getOverviewHref({
    projectId,
    buildTypeId,
    buildId,
    pipelineId,
    runId,
  })}?${currentSearch.toString()}`
}
export const getHasBuildConfigurations: (
  state: State,
  projectId: ProjectId,
  onlyFavorites: boolean | null | undefined,
) => boolean = (state, projectId, onlyFavorites) => {
  const buildTypes =
    (onlyFavorites === true ? getOverviewProject : getProject)(state, projectId)?.buildTypes
      ?.buildType ?? emptyArray
  const pipelines =
    (onlyFavorites === true ? getOverviewProject : getProject)(state, projectId)?.pipelines
      ?.pipeline ?? emptyArray

  return buildTypes.length > 0 || pipelines.length > 0
}

export const getProjectId = (
  state: State,
  node?: ProjectOrBuildTypeNode | null,
): ProjectId | null | undefined => {
  if (node == null) {
    return null
  }

  switch (node.nodeType) {
    case 'project':
      return node.id

    case 'bt':
      return getBuildType(state, node.id)?.projectId

    default:
      return null
  }
}

export const getProjectIdFromBuildId = (
  state: State,
  buildId: BuildId | null | undefined,
): ProjectId | undefined => {
  const build = getBuild(state, buildId)
  return getBuildType(state, build?.buildType)?.projectId
}

const getAnchorBuild = createSelector(
  (state: State, id?: BuildId | null) => getBuild(state, id),
  getBuildsHash,
  (build, builds) => (build?.anchorBuild ? builds[build.anchorBuild] : null),
)

export const getPipelineBatchIdFromBuildId = createSelector(
  getBuild,
  getAnchorBuild,
  (build, anchorBuild) => (anchorBuild?.parallelized && build ? stringifyId(build.id) : null),
)

export const getPipelineJobIdFromBuildId = createSelector(
  getBuild,
  getAnchorBuild,
  (build, anchorBuild) => {
    const targetBuild = anchorBuild?.parallelized ? anchorBuild : build
    return targetBuild?.pipelineRunJob
  },
)

export const getPipelineRunIdFromBuildId = createSelector(
  getBuild,
  getAnchorBuild,
  getPipelineRunJobsHash,
  (build, anchorBuild, jobs) => {
    const targetBuild = anchorBuild?.parallelized ? anchorBuild : build
    const jobId = targetBuild?.pipelineRunJob
    return jobId && jobs[jobId] ? jobs[jobId].pipelineRun : targetBuild?.pipelineRun
  },
)

export const getPipelineIdFromBuildId = createSelector(
  getPipelineRunIdFromBuildId,
  getPipelineRunsHash,
  (runId, runs) => (runId ? runs[runId]?.pipeline : null),
)

export const makeGetProjectBuildTypes: () => (
  state: State,
  id: ProjectId,
) => ReadonlyArray<BuildType> = () =>
  createSelector(getProject, getBuildTypesHash, (project, buildTypes) => {
    const buildTypeIDs = project?.buildTypes?.buildType || emptyArray

    if (buildTypeIDs.length === 0) {
      return emptyArray
    }

    return buildTypeIDs.map(buildTypeId => buildTypes[buildTypeId]).filter(Boolean)
  })
export const getIsReadOnly = (state: State, projectId: ProjectId | null | undefined): boolean =>
  getProject(state, projectId)?.readOnlyUI?.value === true
const MAX_QUEUED_COUNT_FOR_AUTO_EXPAND = 3
export const shouldAutoExpandQueued = (state: State, key: StatusKey): boolean =>
  !getShowQueuedBuildsInBuildsList(state) &&
  (getStatusByKey(state, key)?.queued ?? 0) <= MAX_QUEUED_COUNT_FOR_AUTO_EXPAND
export const getAutoExpandQueuedBuilds = (state: State, statusKey: StatusKey): boolean =>
  state.queuedToggler.autoExpand[statusKey] === true
export const getHasTriggeredByMeBuilds = (
  state: State,
  buildTypeId: BuildTypeId | null | undefined,
): boolean =>
  buildTypeId != null && state.queuedToggler.hasTriggeredByMeBuilds[buildTypeId] === true

export const getIsAgentTypeManageable = (state: State, agentTypeId: AgentTypeId): boolean =>
  isCloudImageManageable(getSingleCloudImage(state, agentTypeId))

const availibleTabNamesOnlyForQueuedBuild = [
  BuildPageTabNamesEnum.queuedBuildCompatibilityTab,
  BuildPageTabNamesEnum.queuedBuildOverviewTab,
]

export const tabExists = (
  state: State,
  tabParamsKey: TabParamsKey,
  tabId: TabId | null | undefined,
): boolean => {
  const allowedTabs = getTabs(state, tabParamsKey)
  return allowedTabs.some(item => item.id === tabId)
}
export const getDefaultProjectTab = (state: State, projectId: ProjectId): TabId => {
  const parameters = state.entities.projectParameters[projectId]?.parameters
  const tabParamsKey = getTabParamsKey({
    projectId,
  })
  const preferredTab = getPropertyFromList(BuildTypeProperties.DEFAULT_PROJECT_TAB, parameters)

  if (preferredTab && tabExists(state, tabParamsKey, preferredTab)) {
    return preferredTab
  }

  return ProjectPageTabNamesEnum.OVERVIEW
}

const getDefaultBuildTypeTab = (state: State, buildTypeId: BuildTypeId): TabId => {
  const parameters = state.entities.buildTypeParameters[buildTypeId]?.parameters
  const tabParamsKey = getTabParamsKey({
    buildTypeId,
  })
  const preferredTab = getPropertyFromList(BuildTypeProperties.DEFAULT_BUILDTYPE_TAB, parameters)

  if (preferredTab && tabExists(state, tabParamsKey, preferredTab)) {
    return preferredTab
  }

  return BuildTypePageTabNamesEnum.OVERVIEW
}

const getIsAvailableBuildTab: (
  state: State,
  buildId: BuildId | undefined | null,
  tab: TabId | null | undefined,
) => boolean = (state, buildId, tab) => {
  const queued = getBuild(state, buildId)?.state === 'queued'

  if (buildId == null || tab == null) {
    return false
  }

  return queued ? true : !availibleTabNamesOnlyForQueuedBuild.includes(tab)
}

export const getCurrentBuildTypeTab = (state: State): TabId | null => state.buildTypeTab
export const getCurrentProjectPageTab = (state: State): TabId | null => state.projectPage.tab
export const getCurrentBuildTab = (state: State): TabId | null => state.buildTab
const projectTabPredefined = (tab: TabId | null | undefined): boolean =>
  tab != null && Object.values(ProjectPageTabNamesEnum).includes(tab)
const buildTypeTabPredefined = (tab: TabId | null | undefined): boolean =>
  tab != null && Object.values(BuildTypePageTabNamesEnum).includes(tab)
const buildTabPredefined = (tab: TabId | null | undefined): boolean =>
  tab != null && Object.values(BuildPageTabNamesEnum).includes(tab)

interface CalcNextTabParams extends TabParams {
  tab: TabId | null | undefined
}

export const calcNextTab = (state: State, tabParams: CalcNextTabParams): TabId => {
  const {tab, buildId, buildTypeId, projectId} = tabParams
  const tabParamsKey = getTabParamsKey(tabParams)
  let defaultTab = BuildPageTabNamesEnum.OVERVIEW
  let tabPredefined = buildTabPredefined(tab)
  let availableTab = getIsAvailableBuildTab(state, buildId, tab)

  if (buildTypeId != null) {
    defaultTab = getDefaultBuildTypeTab(state, buildTypeId)
    tabPredefined = buildTypeTabPredefined(tab)
    availableTab = true
  }

  if (projectId != null) {
    defaultTab = getDefaultProjectTab(state, projectId)
    tabPredefined = projectTabPredefined(tab)
    availableTab = true
  }

  return tab && availableTab && (tabExists(state, tabParamsKey, tab) || tabPredefined)
    ? tab
    : defaultTab
}
